import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Satellite,
  Users,
  Send,
  CheckCircle,
  Globe,
  Building2,
  MessageSquare,
} from "lucide-react";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

const Contact = () => {
  const { t, i18n } = useTranslation("common");
  const { addContact } = useStore();
  const isRtl = (i18n.language || "ar") === "ar";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    inquiryType: "general",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const inquiryTypes = [
    {
      value: "general",
      label: t("contact.inquiryTypes.general"),
      icon: MessageSquare,
    },
    {
      value: "partnership",
      label: t("contact.inquiryTypes.partnership"),
      icon: Users,
    },
    {
      value: "investment",
      label: t("contact.inquiryTypes.investment"),
      icon: Building2,
    },
    { value: "careers", label: t("contact.inquiryTypes.careers"), icon: Users },
    { value: "media", label: t("contact.inquiryTypes.media"), icon: Globe },
    {
      value: "technical",
      label: t("contact.inquiryTypes.technical"),
      icon: Satellite,
    },
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: t("contact.info.headquarters.title"),
      details: [t("contact.info.headquarters.address1")],
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Phone,
      title: t("contact.info.phones.title"),
      details: [t("contact.info.phones.phone1")],
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: Mail,
      title: t("contact.info.emails.title"),
      details: [t("contact.info.emails.info")],
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: Clock,
      title: t("contact.info.hours.title"),
      details: [t("contact.info.hours.weekdays")],
      color: "from-orange-500 to-red-500",
    },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim())
      newErrors.name = t("contact.errors.nameRequired");
    if (!formData.email.trim())
      newErrors.email = t("contact.errors.emailRequired");
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = t("contact.errors.emailInvalid");
    if (!formData.subject.trim())
      newErrors.subject = t("contact.errors.subjectRequired");
    if (!formData.message.trim())
      newErrors.message = t("contact.errors.messageRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const submission = {
        ...formData,
        timestamp: new Date().toISOString(),
        id: Date.now().toString(),
      };

      // Prepare email body
      const emailBody = `
رسالة تواصل جديدة من موقع NSPO
=====================================

الاسم: ${formData.name}
البريد الإلكتروني: ${formData.email}
رقم الهاتف: ${formData.phone || "غير محدد"}
الشركة: ${formData.company || "غير محدد"}
نوع الاستفسار: ${formData.inquiryType}
الموضوع: ${formData.subject}

الرسالة:
${formData.message}

التاريخ والوقت: ${new Date().toLocaleString("ar-EG")}
=====================================

---
New Contact Form Submission from NSPO Website
=====================================

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || "Not specified"}
Company: ${formData.company || "Not specified"}
Inquiry Type: ${formData.inquiryType}
Subject: ${formData.subject}

Message:
${formData.message}

Date & Time: ${new Date().toLocaleString("en-US")}
=====================================
      `.trim();

      const emailSubject = encodeURIComponent(
        `رسالة جديدة من الموقع: ${formData.subject} | New Form: ${formData.subject}`
      );
      const encodedBody = encodeURIComponent(emailBody);
      const emailTo = "nspo@nspo.com.eg";

      // Open mailto link
      const mailtoLink = `mailto:${emailTo}?subject=${emailSubject}&body=${encodedBody}`;
      window.location.href = mailtoLink;

      // Store submission locally
      addContact(submission);

      // Show success message
      setTimeout(() => {
        setSubmitted(true);
      }, 500);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <Card className="max-w-2xl w-full">
          <Card.Content className="p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t("contact.success.title")}
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              {t("contact.success.message")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setSubmitted(false)}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
              >
                {t("contact.success.anotherMessage")}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/")}
              >
                {t("contact.success.backHome")}
              </Button>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageSquare className="w-4 h-4" />
            {t("contact.header.badge")}
          </div>
          <h1 className="text-5xl md:text-6xl from-transparent via-blue-600 to-transparent bg-clip-text mb-6">
            {t("contact.header.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("contact.header.description")}
          </p>
        </div>

        {/* Contact Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => {
            const IconComponent = info.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${info.color}`}></div>
                <Card.Content className="p-6 text-center">
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${info.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {info.title}
                  </h3>
                  <div className="space-y-1">
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-sm text-gray-600">
                        {detail}
                      </p>
                    ))}
                  </div>
                </Card.Content>
              </Card>
            );
          })}
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 gap-12">
          {/* Form */}
          <div>
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 p-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  أرسل لنا رسالة
                </h2>
                <p className="text-blue-100">
                  يرجى ملء النموذج التالي وسنعاود الاتصال بك في أقرب وقت.
                </p>
              </div>
              <Card.Content className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Inquiry Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t("contact.form.inquiryType")}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {inquiryTypes.map((type) => {
                        const IconComponent = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() =>
                              handleInputChange("inquiryType", type.value)
                            }
                            className={`p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
                              formData.inquiryType === type.value
                                ? "border-blue-500 bg-blue-50 text-blue-700"
                                : "border-gray-200 hover:border-gray-300 text-gray-600"
                            }`}
                          >
                            <IconComponent className="w-4 h-4" />
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t("contact.form.fullName")}
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      error={errors.name}
                      placeholder={t("contact.form.namePlaceholder")}
                      required
                    />

                    <Input
                      label={t("contact.form.email")}
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      error={errors.email}
                      placeholder={t("contact.form.emailPlaceholder")}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t("contact.form.phone")}
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      placeholder={t("contact.form.phonePlaceholder")}
                    />

                    <Input
                      label={t("contact.form.company")}
                      value={formData.company}
                      onChange={(e) =>
                        handleInputChange("company", e.target.value)
                      }
                      placeholder={t("contact.form.companyPlaceholder")}
                    />
                  </div>

                  <Input
                    label={t("contact.form.subject")}
                    value={formData.subject}
                    onChange={(e) =>
                      handleInputChange("subject", e.target.value)
                    }
                    error={errors.subject}
                    placeholder={t("contact.form.subjectPlaceholder")}
                    required
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("contact.form.message")}
                    </label>
                    <textarea
                      rows={5}
                      value={formData.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                      placeholder={t("contact.form.messagePlaceholder")}
                      required
                    />
                    {errors.message && (
                      <p className="text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {t("contact.form.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t("contact.form.send")}
                      </>
                    )}
                  </Button>
                </form>
              </Card.Content>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
